import Vue from "vue";
import Vuex from "vuex";
import instance from "../http";
import config from "@/config";
import {
  Shop,
  RootState,
  MallInfo,
  AuthError,
  LoadStatus,
} from "@/types/store";
import { SettlementResult } from "@/types/settlement";
import {
  clearAllAuthStorage,
  clearLegacyAuthStorage,
  createAuthStoragePlugin,
  loadLegacyAuthState,
  loadPersistedAuthState,
} from "@/utils/authStorage";
import { fetchPgFeeRatesFromServer } from "@/utils/pgFee";
import eventBus from "@/eventBus";

export type { Shop, RootState as SettlementState };

Vue.use(Vuex);

const restoredAuth = loadPersistedAuthState() || loadLegacyAuthState();

const defaultState: RootState = {
  access_token: restoredAuth?.access_token || "",
  refresh_token: restoredAuth?.refresh_token || "",
  mall_info: restoredAuth?.mall_info || null,
  shop: (restoredAuth?.shop as Shop) || ({} as Shop),
  results: [] as SettlementResult[],
  authChecked: false,
  signing: false,
  authError: null,
  shopProfileStatus: restoredAuth?.shop?.mall_id ? "ready" : "idle",
  pgFeeStatus: "idle",
  operationalReady: Boolean(restoredAuth?.mall_info),
  authGeneration: 0,
};

const store = new Vuex.Store<RootState>({
  state: defaultState,
  getters: {
    mallId(state): string {
      return state.mall_info?.mall_id || "";
    },
    isAuthenticated(state): boolean {
      return Boolean(
        state.mall_info && (state.access_token || state.refresh_token)
      );
    },
  },
  mutations: {
    setTokens(
      state,
      data: { access_token?: string; refresh_token?: string } | string
    ) {
      if (typeof data === "string") {
        state.access_token = data;
        state.refresh_token = "";
      } else if (data) {
        if (data.access_token !== undefined) {
          state.access_token = data.access_token;
        }
        if (data.refresh_token !== undefined) {
          state.refresh_token = data.refresh_token;
        }
      }
    },
    beginNewSession(
      state,
      tokens: { access_token: string; refresh_token: string }
    ) {
      state.authGeneration += 1;
      state.access_token = tokens.access_token;
      state.refresh_token = tokens.refresh_token;
      state.mall_info = null;
      state.shop = {} as Shop;
      state.results = [];
      state.authChecked = false;
      state.signing = true;
      state.authError = null;
      state.shopProfileStatus = "idle";
      state.pgFeeStatus = "idle";
      state.operationalReady = false;
    },
    setMallInfo(state, mallInfo: MallInfo | null) {
      state.mall_info = mallInfo;
      if (mallInfo) state.authError = null;
    },
    setShop(state, data: Shop) {
      state.shop = data || ({} as Shop);
    },
    clearResults(state) {
      state.results = [];
    },
    addResult(state, data: SettlementResult) {
      state.results.push(data);
    },
    setAuthChecked(state, checked: boolean) {
      state.authChecked = checked;
    },
    setSigning(state, signing: boolean) {
      state.signing = signing;
    },
    setAuthError(state, error: AuthError) {
      state.authError = error;
    },
    setShopProfileStatus(state, status: LoadStatus) {
      state.shopProfileStatus = status;
    },
    setPgFeeStatus(state, status: LoadStatus) {
      state.pgFeeStatus = status;
    },
    setOperationalReady(state, ready: boolean) {
      state.operationalReady = ready;
    },
    resetSession(state, reason: AuthError = null) {
      state.authGeneration += 1;
      state.access_token = "";
      state.refresh_token = "";
      state.mall_info = null;
      state.shop = {} as Shop;
      state.results = [];
      state.authChecked = true;
      state.signing = false;
      state.authError = reason;
      state.shopProfileStatus = "idle";
      state.pgFeeStatus = "idle";
      state.operationalReady = false;
    },
  },
  actions: {
    startNewSession({ commit }, tokens) {
      commit("beginNewSession", tokens);
      eventBus.$emit("auth-session-reset");
    },
    async verifyIdentity({ state, commit }): Promise<MallInfo | null> {
      const generation = state.authGeneration;
      try {
        const response = await instance.get(`${config.apiUri}/api/me`);
        if (generation !== state.authGeneration) return null;
        const mallInfo = response.data?.mall_info as MallInfo | undefined;
        if (!mallInfo?.mall_id || !mallInfo.shop_no) {
          throw new Error("Invalid /api/me response");
        }
        const normalized = {
          mall_id: mallInfo.mall_id,
          shop_no: String(mallInfo.shop_no),
        };
        commit("setMallInfo", normalized);
        clearLegacyAuthStorage();
        // 신원 확인(/api/me) 완료 즉시 정산 기능을 사용할 수 있도록 operationalReady 선언!
        commit("setOperationalReady", true);
        return normalized;
      } catch (error) {
        if (generation !== state.authGeneration) {
          console.error("Failed to verify admin identity:", error);
        }
        return null;
      }
    },
    async initializeOperationalContext({ state, commit, dispatch }) {
      const mallInfo = state.mall_info;
      if (!mallInfo) return false;
      const generation = state.authGeneration;

      // 신원 확인이 이미 완료되었으므로 대시보드 화면 및 정산 쿼리는 즉시 시작 (Non-blocking)
      commit("setOperationalReady", true);
      if (!state.shop?.mall_id || state.shop.mall_id !== mallInfo.mall_id) {
        commit("setShopProfileStatus", "loading");
      }
      commit("setPgFeeStatus", "loading");

      // PG 수수료 동기화는 화면 진입을 가로막지 않도록 백그라운드에서 비동기 처리
      void fetchPgFeeRatesFromServer()
        .then(() => {
          if (generation !== state.authGeneration) return;
          commit("setPgFeeStatus", "ready");
          eventBus.$emit("pg-fee-updated");
        })
        .catch((error) => {
          if (generation !== state.authGeneration) return;
          console.error("Failed to sync PG fee rates:", error);
          commit("setPgFeeStatus", "error");
          eventBus.$emit("snackbar", {
            message:
              "PG 수수료 설정을 불러오지 못해 저장된 값 또는 기본값을 사용합니다.",
          });
        });

      // 상점 정보 확인은 백그라운드에서 비동기 조회 (2.5초짜리 외부 API가 화면을 가로막지 않음)
      try {
        const shopResponse = await instance.get("/admin/shop");
        if (generation !== state.authGeneration) return false;

        const shop = shopResponse.data as Shop;
        if (
          shop.mall_id !== mallInfo.mall_id ||
          String(shop.shop_no) !== mallInfo.shop_no
        ) {
          await dispatch("invalidateSession", "identity-mismatch");
          return false;
        }

        commit("setShop", shop);
        commit("setShopProfileStatus", "ready");
        return true;
      } catch (error) {
        if (generation !== state.authGeneration) return false;
        console.error("Failed to load shop info in background:", error);
        commit("setShopProfileStatus", "error");
        return false;
      }
    },
    async retryOperationalContext({ dispatch }) {
      return dispatch("initializeOperationalContext");
    },
    async invalidateSession({ commit }, reason: AuthError) {
      commit("resetSession", reason);
      clearAllAuthStorage();
      eventBus.$emit("auth-session-reset");
    },
    async signOut({ dispatch }) {
      await dispatch("invalidateSession", null);
    },
  },
  plugins: [createAuthStoragePlugin()],
});

export default store;
